import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { StorageService } from '@core/services/storages.service';
import { AuthStateService } from '@core/services/auth-state.service';
import { SESSION_ACCESS_KEY, SESSION_LEGACY_KEY, SESSION_REFRESH_KEY, SESSION_TOKEN_KEYS, REFRESH_API_PATH, REFRESH_ORBIT_API_PATH } from '@core/constants/token-storage.constants';
import { getMillisecondsUntilExpiry } from '@core/utils/jwt-decoder';
import { isLegacyTokenOnlyMode } from '@core/utils/legacy-token-mode';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';

/**
 * Interceptor para módulos del ecosistema configuration-ia.
 *
 * Lee el accessToken (JWT Bearer) del sessionStorage. El token es capturado en el
 * arranque por APP_INITIALIZER (CoreModule) y actualizado en cada navegación por
 * UrlTokenService. Nunca sobreescribe sessionStorage — solo lo lee.
 *
 * No aplica si la petición ya lleva Authorization (ej. chatbot con ChatBotToken).
 * No aplica al endpoint de refresh — este usa el refreshToken en el body.
 *
 * Prioridad: urlAccessToken → urlLegacyToken. Con CTDLegacyTokenOnly en client-tokens y
 * configuration-ia, solo el token legacy.
 */

export const accessTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.headers.has('Authorization')) return next(req);
  if (req.url.includes(REFRESH_API_PATH))       return next(req);
  if (req.url.includes(REFRESH_ORBIT_API_PATH)) return next(req);

  const router         = inject(Router);
  const storageService = inject(StorageService);
  const authState      = inject(AuthStateService);
  const trunkBase = inject(TrunkBaseConfigurationService);
  const usesLegacyOnly = () => isLegacyTokenOnlyMode(
    router.getCurrentNavigation()?.extractedUrl.toString() ?? router.url,
    trunkBase.CTDLegacyTokenOnly
  );
  const legacyOnly = usesLegacyOnly();

  const rawAccess = storageService.getSessionStorage(SESSION_ACCESS_KEY);
  const rawLegacy = storageService.getSessionStorage(SESSION_LEGACY_KEY);
  const accessToken = typeof rawAccess === 'string' && rawAccess ? rawAccess : null;
  const legacyToken = typeof rawLegacy === 'string' && rawLegacy ? rawLegacy : null;

  // Legacy-only: a stale JWT from a previous session must not win over the validated legacy token.
  const bearerToken = (legacyOnly ? legacyToken : accessToken || legacyToken) || '';

  if (!bearerToken) return next(req);

  return next(req.clone({ setHeaders: { Authorization: `Bearer ${bearerToken}` } })).pipe(
    catchError((err: unknown) => {
      if (legacyOnly || usesLegacyOnly() || !(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      const rawRefresh      = storageService.getSessionStorage(SESSION_REFRESH_KEY);
      const refreshToken    = typeof rawRefresh === 'string' && rawRefresh ? rawRefresh : null;
      const msLeft          = refreshToken ? (getMillisecondsUntilExpiry(refreshToken) ?? -1) : -1;
      const canStillRefresh = msLeft > 0;

      if (!canStillRefresh) {
        SESSION_TOKEN_KEYS.forEach(key => storageService.removeSessionStorage(key));
        authState.clear();
        router.navigateByUrl('/session-expired');
      }

      return throwError(() => err);
    })
  );
};
